import { addDays, addMinutes, differenceInMinutes } from "date-fns";
import gridPalkkiClass from "src/data/gridPalkki/gridPalkkiClass";
import { keikka_doesKeikkaOverlap } from "src/data/keikka/KeikkaFunctions";
import { socketOnSave } from "src/data/system/socketClass";
import { gridKeikka_copyJSON } from "src/pages/Grid/GridKeikka/gridKeikkaFunctions";

const MAX_ROWS = 10; // max rows inside vehicle
export function gridContext_insertKeikkaIntoVehicleAndSetRowNum(keikka, vehicle, time) {
  // If keikka is not within the selected time range, set its row number to 1 and return, do not process further
  if (keikka.timeEnd < time.starttime || keikka.timeStart > time.endtime) {
    keikka.vehicleRowNum = 1;
    return 1;
  }

  // show vehicle if keikka is assigned to it within the selected time range
  vehicle.showInGridCalc = true;

  // Check each row to see if the keikka can fit into it
  if (!vehicle.keikkaGroup) vehicle.keikkaGroup = Array.from({ length: MAX_ROWS }, () => []);
  for (let vehicleRowNum = 1; vehicleRowNum <= MAX_ROWS; vehicleRowNum++) {
    const keikkasInSameRow = vehicle.keikkaGroup.filter((k) => k.vehicleRowNum === vehicleRowNum);

    // Check if the keikka overlaps with any other keikka in the same row
    const doesOverlap = keikkasInSameRow.some((existingKeikka) =>
      keikka_doesKeikkaOverlap(keikka, existingKeikka)
    );

    // If the keikka does not overlap, add it to the row
    if (!doesOverlap) {
      keikka.vehicleRowNum = vehicleRowNum;
      vehicle.keikkaGroup.push(keikka);
      if (!vehicle.grid_rowCount || vehicle.grid_rowCount < vehicleRowNum)
        vehicle.grid_rowCount = vehicleRowNum;
      return vehicleRowNum;
    }
  }
}

export function gridContext_setFetchDatesViaLoadedDates(
  time,
  loadedDates_containsDate,
  loadedDates_addDay
) {
  let _startDate = time.starttime;
  let _endDate = time.endtime;
  if (!loadedDates_containsDate(_startDate)) loadedDates_addDay(_startDate);

  let containsDay1 = loadedDates_containsDate(addDays(_startDate, -1));
  if (!containsDay1) {
    let day1 = addDays(_startDate, -1);
    loadedDates_addDay(day1);
    _startDate = day1;
  }

  let containsDay2 = loadedDates_containsDate(addDays(_endDate, 1));
  if (!containsDay2) {
    let day2 = addDays(_endDate, 1);
    loadedDates_addDay(day2);
    _endDate = day2;
  }

  return { _startDate, _endDate };
}

const compareSortNo = (a, b) => a.sortNo - b.sortNo;
const compareVehicleNo = (a, b) => a.vehicleNo - b.vehicleNo;
const compareVehicleNimi = (a, b) => (a.vehicleNimi || "").localeCompare(b.vehicleNimi || "");
const compareVehiclePuomi = (a, b) => a.vehiclePuomi - b.vehiclePuomi;
const compareVehicleRegNo = (a, b) => a.vehicleRegNo.localeCompare(b.vehicleRegNo);

export function gridContext_doSortVehicles(gridSettings, vehicles) {
  switch (gridSettings.sortId) {
    case 0:
      vehicles.sort(compareSortNo);
      break;
    case 1:
      vehicles.sort(compareVehicleNo);
      break;
    case 2:
      vehicles.sort(compareVehicleNimi);
      break;
    case 3:
      vehicles.sort(compareVehiclePuomi);
      break;
    case 4:
      vehicles.sort(compareVehicleRegNo);
      break;
  }
}

export function gridContext_doMergeKeikat(keikat, _newKeikatFound) {
  const newKeikat = [...keikat];
  const keikkaIdToIndex = new Map(newKeikat.map((k, i) => [k.keikkaId, i]));
  _newKeikatFound.forEach((_newKeikka) => {
    const index = keikkaIdToIndex.get(_newKeikka.keikkaId);
    if (index !== undefined) newKeikat[index] = _newKeikka;
    else newKeikat.push(_newKeikka);
  });
  return newKeikat;
}

export function gridContext_doInsertTempPalkit(vehicles, _keikat, userAsiakas, time, gridSettings) {
  // remove any existing temp palkit
  const keikat = _keikat.filter((k) => !k.isTemp);
  // sort vehicles.keikkaGroup by timeStart
  vehicles.forEach((vehicle) => {
    if (vehicle.keikkaGroup) vehicle.keikkaGroup.sort((a, b) => a.timeStart - b.timeStart);
  });
  vehicles.forEach((vehicle) => {
    if (!vehicle.showInGridCalc) return;
    if (vehicle.vehicleId === 0) return; // do not create temp palkki for temp vehicle
    if (vehicle.useNoDriverBar === true && vehicle.driverCount === 0) {
      // startTime at 6, endTime at 17
      let _starttime = new Date(time.starttime);
      _starttime.setHours(
        Math.min(
          gridSettings.keikkaFirstHour || gridSettings.activeHourStart,
          gridSettings.activeHourStart
        ),
        0,
        0,
        0
      );
      let _endtime = new Date(time.starttime);
      _endtime.setHours(
        Math.max(
          gridSettings.keikkaLastHour || gridSettings.activeHourEnd,
          gridSettings.activeHourEnd
        ),
        0,
        0,
        0
      );
      const gridPalkki = gridPalkkiClass.createTempPalkkiKeikka(
        _starttime,
        _endtime,
        vehicle.vehicleId,
        "Ei kuljettajaa!",
        "",
        userAsiakas.asiakasId,
        10,
        "",
        "_vehicle" + vehicle.vehicleId + "_noDriver_" + time.yyyymmdd
      );
      gridPalkki.hideToolTip = true;

      gridContext_insertKeikkaIntoVehicleAndSetRowNum(gridPalkki, vehicle, time);
      keikat.push(gridPalkki);
    }
    if (vehicle.showInGridCalc && vehicle.asiakasId !== userAsiakas.asiakasId) {
      let _lastKeikkaEndTime = new Date(time.starttime);
      _lastKeikkaEndTime.setHours(
        Math.min(
          gridSettings.keikkaFirstHour || gridSettings.activeHourStart,
          gridSettings.activeHourStart
        ),
        0,
        0,
        0
      );

      vehicle.keikkaGroup.forEach((keikka) => {
        const _thisKeikkaStart = new Date(keikka.timeStart);
        const minutes = (keikka.timeStart - _lastKeikkaEndTime) / 1000 / 60;
        if (minutes >= 100) {
          const gpc = gridPalkkiClass.createTempPalkkiKeikka(
            addMinutes(_lastKeikkaEndTime, 45),
            addMinutes(_thisKeikkaStart, -45),
            vehicle.vehicleId,
            "",
            "",
            userAsiakas.asiakasId
          );
          gridContext_insertKeikkaIntoVehicleAndSetRowNum(gpc, vehicle, time);
          keikat.push(gpc);
        }
        _lastKeikkaEndTime = new Date(keikka.timeEnd);
      });
      let _endtime = new Date(_lastKeikkaEndTime);

      _endtime.setHours(
        Math.max(
          gridSettings.keikkaLastHour || gridSettings.activeHourEnd,
          gridSettings.activeHourEnd
        ),
        0,
        0,
        0
      );
      // if time difference is above 90 minutes, add a temp palkki
      const _minutes = (_endtime - _lastKeikkaEndTime) / 1000 / 60;

      if (_minutes > 100 && _lastKeikkaEndTime < _endtime) {
        const gpc = gridPalkkiClass.createTempPalkkiKeikka(
          addMinutes(_lastKeikkaEndTime, 45),
          addMinutes(_endtime, -45),
          vehicle.vehicleId,
          "",
          "",
          userAsiakas.asiakasId
        );
        gridContext_insertKeikkaIntoVehicleAndSetRowNum(gpc, vehicle, time);
        keikat.push(gpc);
      }
    }
  });
  return { vehicles, keikat };
}

export function createVehicleKeikkaCache() {
  const cache = new Map();

  return {
    // Check if vehicle needs processing by comparing its keikkas
    needsProcessing: (vehicleId, keikatForVehicle) => {
      const cachedKeikkaIds = cache.get(vehicleId);

      if (!cachedKeikkaIds) return true;

      const currentKeikkaIds = new Set(keikatForVehicle.map((k) => k.keikkaId || `p${k.palkkiId}`));

      // Compare current keikkas with cached keikkas
      if (currentKeikkaIds.size !== cachedKeikkaIds.size) return true;

      for (const id of currentKeikkaIds) {
        if (!cachedKeikkaIds.has(id)) return true;
      }

      return false;
    },

    // Update cache after processing a vehicle
    updateCache: (vehicleId, keikatForVehicle) => {
      cache.set(vehicleId, new Set(keikatForVehicle.map((k) => k.keikkaId || `p${k.palkkiId}`)));
    },

    // Clear cache
    clearCache: () => {
      cache.clear();
    },
  };
}

export function gridContext_doInsertKeikkasIntoVehicles(
  vehicles,
  newKeikat,
  gridSettings,
  time,
  vehicleKeikkaCache
) {
  const vehicleIdToKeikat = new Map();

  // Group keikkas by vehicleId for faster lookup
  newKeikat.forEach((k) => {
    if (k.timeEnd < time.starttime || k.timeStart > time.endtime) return;
    const keikat = vehicleIdToKeikat.get(k.vehicleId) || [];
    keikat.push(k);
    vehicleIdToKeikat.set(k.vehicleId, keikat);
  });

  // Create cache if it doesn't exist
  const cache = vehicleKeikkaCache || createVehicleKeikkaCache();

  return vehicles.map((vehicle) => {
    // Basic vehicle properties always need to be set
    vehicle.grid_rowHeight = gridSettings.rowHeight;

    const keikatForVehicle = vehicleIdToKeikat.get(vehicle.vehicleId) || [];

    vehicle.keikkaGroup = [];
    vehicle.grid_rowCount = 1;

    const insertedKeikkaIds = new Set();
    keikatForVehicle.forEach((_keikka) => {
      if (_keikka.timeEnd < time.starttime || _keikka.timeStart > time.endtime) return;
      const keikkaId = _keikka.keikkaId || `p${_keikka.palkkiId}`;
      if (insertedKeikkaIds.has(keikkaId)) return;

      insertedKeikkaIds.add(keikkaId);
      gridContext_insertKeikkaIntoVehicleAndSetRowNum(_keikka, vehicle, time);
    });

    // Update cache with processed vehicle
    cache.updateCache(vehicle.vehicleId, keikatForVehicle);

    return vehicle;
  });
}

export function gridContext_doInsertDayDriverIntoVehicleFromVehicleDayDrivers(
  vehicles,
  vehicleDriverDays,
  driverLookup
) {
  for (let i = 0; i < vehicles.length; i++) {
    const vehicle = vehicles[i];
    const vehicleDrivers = vehicleDriverDays.filter((d) => d.vehicleId === vehicle.vehicleId);
    if (vehicleDrivers.length > 0)
      vehicle.dayDriver = driverLookup[vehicleDrivers[0].driverPersonId];
    else vehicle.dayDriver = null;
  }
}

export async function gridKeikka_saveToDB(gridKeikka, token, socket, personId) {
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: gridKeikka_copyJSON(gridKeikka, true),
  };

  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/grid/keikka/save/" + gridKeikka.keikkaId,
    requestOptions
  );
  // send socket event
  const s = new socketOnSave(
    personId,
    socket,
    gridKeikka.ownerAsiakasId,
    "gridKeikka",
    "save",
    gridKeikka.keikkaId
  );
  s.keikkaId = gridKeikka.keikkaId;
  s.gridKeikka = gridKeikka;
  s.emitSave();

  return response;
}

export function getAttachedPalkit(
  keikat, // Array of all keikkas
  gridKeikka, // The keikka to check against
  timeStartThreshold = 11, // minutes to check for timeStart overlap
  timeEndThreshold = 61 // minutes to check for timeEnd overlap
) {
  if (!Array.isArray(keikat) || typeof gridKeikka !== "object") return [];

  const attachedPalkit = keikat.filter(
    (k) =>
      k.isPalkki &&
      k.keikkaId !== gridKeikka.keikkaId &&
      k.grid_palkkiTypeId !== 11 &&
      (k.keikkaId === gridKeikka.keikkaId ||
        (k.vehicleId === gridKeikka.vehicleId &&
          Math.abs(differenceInMinutes(k.timeStart, gridKeikka.timeStart)) < timeStartThreshold &&
          Math.abs(differenceInMinutes(k.timeEnd, gridKeikka.timeEnd)) < timeEndThreshold))
  );
  return attachedPalkit;
}
